import { ApolloLink, Observable } from "@apollo/client"
import { readEducationPersonaKey } from "./personaStore"

// Member fields that non-education queries (ProfileBriefing, GetMemberForEdit)
// also write to the normalized cache entry the Education pages read.
const PERSONA_CACHE_FIELDS = [
  "activeDays",
  "certified",
  "specialized",
  "type",
  "class",
]

// Persona data and the overlay live in their own chunk, loaded only when a
// persona is stored on a non-production origin, so production never fetches it.
let activePersonaMe = null

export const loadEducationPersonaRuntime = () =>
  import("./personaRuntime").then(runtime => {
    activePersonaMe = runtime.getActiveEducationPersona()?.me ?? null
    return runtime
  })

export const educationPersonaLink = new ApolloLink((operation, forward) => {
  if (!readEducationPersonaKey()) return forward(operation)

  return new Observable(observer => {
    let subscription
    let isClosed = false

    loadEducationPersonaRuntime()
      .then(runtime => {
        if (isClosed) return
        subscription = runtime
          .handlePersonaOperation(operation, forward)
          .subscribe(observer)
      })
      .catch(error => observer.error(error))

    return () => {
      isClosed = true
      if (subscription) subscription.unsubscribe()
    }
  })
})

// Cache-first Education queries skip the link on a cache hit, so cached reads
// of the shared Member fields must also resolve to the persona. The link loads
// the persona before any response is written, so these reads see it.
export const getEducationPersonaTypePolicies = () => {
  if (!readEducationPersonaKey()) return {}

  return {
    Member: {
      fields: PERSONA_CACHE_FIELDS.reduce(
        (fields, fieldName) => ({
          ...fields,
          [fieldName]: {
            read: existing =>
              activePersonaMe && activePersonaMe[fieldName] !== undefined
                ? activePersonaMe[fieldName]
                : existing,
          },
        }),
        {},
      ),
    },
  }
}

export const applyEducationPersonaToUser = async user => {
  if (!user || !readEducationPersonaKey()) return user

  const runtime = await loadEducationPersonaRuntime()
  return runtime.applyPersonaTypeToUser(user)
}
